// `window.Notify` / `window.Toast` — la fabrique de messages d'état côté client.
//
// 🔴 **EXTRAITE DE `controllers/notification_controller.js` le 2026-09-21.** Elle y
// pesait 1 613 des 2 028 octets gzippés du fichier — cinq SVG recopiés en ligne —
// et partait dans tout paquet qui enregistrait le contrôleur. GO4LEARN l'a payée
// le jour où il est passé au message de la suite : **+1 597 octets** sur le budget
// d'une page apprenante (terrain Android Go / 3G), pour une usine qu'aucune vue
// apprenante n'appelle.
//
// ⚠️ **Elle n'est importée que par `application.js`.** Ses quatre appelants sont
// tous côté GO4SCHOOL : `school_setting`, `job_status`, `online_payment`,
// `dsf_generation_progress`.
//
// ⚠️ **Les nœuds qu'elle crée démarrent bien en `translate-x-full opacity-0`**, et
// c'est correct ICI : ils n'existent que parce qu'un script les a créés. Le défaut
// corrigé le même jour portait sur le message rendu par le SERVEUR, qui doit se
// lire sans JavaScript — voir `.flash-enter` dans `entrypoints/application.css`.

const TYPE_ALIASES = {
  notice: "success",
  alert: "error",
};

function normalizeType(type) {
  const key = (type || "info").toString().toLowerCase();
  return TYPE_ALIASES[key] || key;
}

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

// 🔴 **LE MÊME TOAST QUE LE SERVEUR — 2026-09-21.**
//
// Cette fabrique peignait en utilitaires Tailwind (`rounded-[15px]`, `bg-red-50`,
// `text-cta-darker`) et recopiait cinq SVG en ligne. Le serveur, lui, rend
// désormais `.ds-toast` du design system. Deux rendus du MÊME objet qui
// divergent, c'est deux endroits à corriger — et un seul qu'on pense à corriger.
//
// ⚠️ **Le DS dit `danger` là où le flash dit `error`** : la seule traduction à faire.
//
// ⚠️ **Les icônes restent des SVG EN LIGNE ici, et c'est délibéré.** Le partial
// serveur passe par `shared/ds/icon`, qui rend un `<i data-lucide>` habillé au
// chargement de la page. Un nœud créé APRÈS ce passage resterait un `<i>` vide —
// c'est le défaut connu de `lucide_icon`, qui ne lève pas.
const TONE_FOR = { success: "success", error: "danger", warning: "warning", info: "info" };

const TOAST_ICONS = {
  success: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
  danger: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8v5m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
  warning: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4m0 3h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>`,
  info: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 16v-4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
};

function getOrCreateContainer() {
  let container = document.getElementById("flash_messages");
  if (!container) {
    container = document.createElement("div");
    container.id = "flash_messages";
    // ⚠️ Mêmes classes que `shared/_notifications` — la pile est en HAUT à droite.
    container.className =
      "fixed top-6 right-6 z-[9999] w-[min(24rem,calc(100vw-3rem))] space-y-3";
    document.body.appendChild(container);
  }
  return container;
}

function renderNotification(message, type = "info", options = {}) {
  const normalized = normalizeType(type);
  const tone = TONE_FOR[normalized] || "info";
  const duration = options.duration ?? 5000;
  const autoHide = options.autoHide ?? true;

  const node = document.createElement("div");
  // ⚠️ `translate-x-full opacity-0` au départ, et c'est CORRECT ici : ce nœud
  // n'existe que parce qu'un script l'a créé, donc `connect()` le fera entrer.
  // Le message rendu par le SERVEUR, lui, ne démarre jamais caché — il doit se
  // lire sans JavaScript, et `.ds-toast` l'anime tout seul (`ds-pop`).
  node.className =
    `ds-toast ds-toast--${tone} transform translate-x-full opacity-0 transition-all duration-300 ease-out`;
  node.setAttribute("role", tone === "danger" || tone === "warning" ? "alert" : "status");
  node.setAttribute("aria-atomic", "true");
  node.setAttribute("data-controller", "notification");
  node.setAttribute("data-notification-type-value", normalized);
  node.setAttribute("data-notification-duration-value", String(duration));
  node.setAttribute("data-notification-auto-hide-value", String(autoHide));

  node.innerHTML = `
    <span class="ds-toast__bar"></span>
    <span class="ds-toast__icon">${TOAST_ICONS[tone]}</span>
    <div class="ds-toast__body"><div class="ds-toast__title">${escapeHtml(message)}</div></div>
    <button type="button" class="ds-toast__close" data-action="click->notification#close" aria-label="Fermer">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M18 6 6 18M6 6l12 12"/></svg>
    </button>
  `;

  getOrCreateContainer().appendChild(node);
  return node;
}

const Notify = {
  show: (message, type = "info", duration = 5000) =>
    renderNotification(message, type, { duration }),
  success: (message, duration = 5000) =>
    renderNotification(message, "success", { duration }),
  error: (message, duration = 5000) =>
    renderNotification(message, "error", { duration }),
  warning: (message, duration = 5000) =>
    renderNotification(message, "warning", { duration }),
  info: (message, duration = 5000) =>
    renderNotification(message, "info", { duration }),
};

window.Notify = Notify;
// Backward-compatible alias: existing callers use window.Toast.*
window.Toast = Notify;
